import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Info, Plus, Trash2, MapPin, Calendar, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AlertBanner from '../components/common/AlertBanner';
import LoadingSpinner from '../components/common/LoadingSpinner';

const SCHEDULE_VII_CATEGORIES = [
  'Eradicating hunger, poverty and malnutrition',
  'Promoting education',
  'Promoting gender equality and empowering women',
  'Reducing child mortality and improving maternal health',
  'Combating HIV/AIDS, malaria and other diseases',
  'Ensuring environmental sustainability',
  'Employment enhancing vocational skills',
  'Social business projects',
  'Contribution to the Prime Minister\'s National Relief Fund',
  'Rural development projects',
  'Slum area development',
  'Disaster management',
  'Armed forces veterans, war widows and their dependents',
  'Training to promote rural sports',
  'Technology incubators',
  'Rural development projects'
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const PROJECT_TYPES = [
  'Direct Implementation',
  'Partner NGO',
  'Multi-stakeholder'
];

const ProjectCreationWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ngos, setNgos] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    title: '',
    description: '',
    scheduleVIICategory: '',
    subCategory: '',
    projectType: '',
    ngoId: '',
    
    // Step 2: Location
    location: {
      state: '',
      district: '',
      village: ''
    },
    
    // Step 3: Problem Statement & Goals
    problemStatement: '',
    targetBeneficiaries: {
      category: '',
      count: 0,
      demographics: ''
    },
    smartGoals: {
      specific: '',
      measurable: '',
      achievable: '',
      relevant: '',
      timeBound: ''
    },
    expectedOutcomes: [''],
    
    // Step 4: Budget
    totalBudget: 0,
    budgetBreakdown: [
      { category: 'Personnel', amount: 0, percentage: 0 },
      { category: 'Materials', amount: 0, percentage: 0 },
      { category: 'Training', amount: 0, percentage: 0 },
      { category: 'Admin', amount: 0, percentage: 0 }
    ],
    
    // Step 5: Timeline
    startDate: '',
    endDate: '',
    duration: 12
  });

  useEffect(() => {
    fetchNGOs();
  }, []);

  const fetchNGOs = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Fetch match suggestions for verified NGOs
      const response = await fetch(`${apiUrl}/api/matching/suggestions?limit=20&minScore=0`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNgos(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching NGOs:', error);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.title && formData.description && formData.scheduleVIICategory && 
               formData.projectType && formData.ngoId;
      case 2:
        return formData.location.state && formData.location.district;
      case 3:
        return formData.problemStatement && formData.targetBeneficiaries.category &&
               formData.targetBeneficiaries.count > 0 && formData.smartGoals.specific &&
               formData.smartGoals.measurable;
      case 4:
        return formData.totalBudget > 0;
      case 5:
        return formData.startDate && formData.endDate;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      setError(null);
    } else {
      setError('Please fill all required fields before proceeding');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Calculate duration in months
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30));

      const payload = {
        ...formData,
        duration,
        expectedOutcomes: formData.expectedOutcomes.filter(o => o.trim() !== ''),
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString()
      };

      const response = await fetch(`${apiUrl}/api/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const project = await response.json();
        setSuccess(true);
        setTimeout(() => {
          navigate(`/projects/${project.id}`);
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setError('An error occurred while creating the project');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Basic Info', icon: Info },
    { number: 2, title: 'Location', icon: MapPin },
    { number: 3, title: 'Goals & Impact', icon: Check },
    { number: 4, title: 'Budget', icon: DollarSign },
    { number: 5, title: 'Timeline', icon: Calendar }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Create New CSR Project</h1>
          <p className="text-gray-600 mt-1">Follow the steps to set up your project with SMART goals</p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      currentStep > step.number
                        ? 'bg-green-500 text-white'
                        : currentStep === step.number
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step.number ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <step.icon className="w-6 h-6" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-700 mt-2">{step.title}</p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 ${
                      currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <AlertBanner type="error" message={error} onClose={() => setError(null)} className="mb-6" />
        )}

        {/* Success Alert */}
        {success && (
          <AlertBanner 
            type="success" 
            message="Project created successfully! Redirecting..." 
            className="mb-6" 
          />
        )}

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-6">
          {currentStep === 1 && <Step1 formData={formData} updateField={updateField} ngos={ngos} />}
          {currentStep === 2 && <Step2 formData={formData} updateNestedField={updateNestedField} />}
          {currentStep === 3 && <Step3 formData={formData} updateField={updateField} updateNestedField={updateNestedField} />}
          {currentStep === 4 && <Step4 formData={formData} updateField={updateField} />}
          {currentStep === 5 && <Step5 formData={formData} updateField={updateField} />}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            onClick={prevStep}
            variant="outline"
            disabled={currentStep === 1 || loading}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Previous
          </Button>

          {currentStep < 5 ? (
            <Button
              onClick={nextStep}
              className="text-white"
              style={{ background: '#14B8A6' }}
              disabled={!validateStep(currentStep)}
            >
              Next
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="text-white"
              style={{ background: '#10B981' }}
              disabled={loading || !validateStep(currentStep)}
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Create Project'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Step 1: Basic Information
const Step1 = ({ formData, updateField, ngos }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">Basic Information</h2>
    
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Project Title <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={formData.title}
        onChange={(e) => updateField('title', e.target.value)}
        placeholder="e.g., Rural Education Initiative for 500 Children"
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Description <span className="text-red-500">*</span>
      </label>
      <textarea
        value={formData.description}
        onChange={(e) => updateField('description', e.target.value)}
        placeholder="Provide a detailed description of the project..."
        rows={4}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Schedule VII Category <span className="text-red-500">*</span>
      </label>
      <select
        value={formData.scheduleVIICategory}
        onChange={(e) => updateField('scheduleVIICategory', e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      >
        <option value="">Select category...</option>
        {SCHEDULE_VII_CATEGORIES.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Project Type <span className="text-red-500">*</span>
      </label>
      <select
        value={formData.projectType}
        onChange={(e) => updateField('projectType', e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      >
        <option value="">Select type...</option>
        {PROJECT_TYPES.map(type => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Implementing Partner (NGO) <span className="text-red-500">*</span>
      </label>
      <select
        value={formData.ngoId}
        onChange={(e) => updateField('ngoId', e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      >
        <option value="">Select NGO partner...</option>
        {ngos.map(ngo => (
          <option key={ngo.ngoId} value={ngo.ngoId}>
            {ngo.ngoName} (Match Score: {ngo.matchScore.toFixed(0)})
          </option>
        ))}
      </select>
      <p className="text-sm text-gray-500 mt-1">NGOs are ranked by match score with your CSR goals</p>
    </div>
  </div>
);

// Step 2: Location
const Step2 = ({ formData, updateNestedField }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Location</h2>
    
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        State <span className="text-red-500">*</span>
      </label>
      <select
        value={formData.location.state}
        onChange={(e) => updateNestedField('location', 'state', e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      >
        <option value="">Select state...</option>
        {INDIAN_STATES.map(state => (
          <option key={state} value={state}>{state}</option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        District <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={formData.location.district}
        onChange={(e) => updateNestedField('location', 'district', e.target.value)}
        placeholder="Enter district name"
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Village/Area (Optional)
      </label>
      <input
        type="text"
        value={formData.location.village}
        onChange={(e) => updateNestedField('location', 'village', e.target.value)}
        placeholder="Enter village or area name"
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
      />
    </div>
  </div>
);

// Import additional steps
import { Step3, Step4, Step5 } from '../components/wizard/WizardSteps';

export default ProjectCreationWizard;
